// rfCalculations.js - RF Calculation Engine for GChain

export const RFCalc = {
  // Physical constants
  BOLTZMANN: 1.380649e-23, // J/K
  T0: 290, // K (standard temperature for noise calculations)

  // ============================================================================
  // UNIT CONVERSIONS
  // ============================================================================

  dbToLinear: (db) => Math.pow(10, db / 10),

  linearToDb: (linear) => {
    if (linear <= 0) return -Infinity;
    return 10 * Math.log10(linear);
  },

  dbmToWatts: (dbm) => Math.pow(10, (dbm - 30) / 10),

  wattsToDbm: (watts) => {
    if (watts <= 0) return -Infinity;
    return 10 * Math.log10(watts) + 30;
  },

  // ============================================================================
  // CASCADED ANALYSIS
  // ============================================================================

  /**
   * Calculate cascaded gain (simple sum in dB)
   */
  cascadeGain: (stages) => {
    return stages.reduce((sum, stage) => sum + (stage.gain || 0), 0);
  },

  /**
   * Friis noise figure formula for cascaded stages
   * stages = [{gain: dB, nf: dB}, ...]
   */
  cascadeNoiseFigure: (stages) => {
    if (stages.length === 0) return 0;

    let nfLinear = RFCalc.dbToLinear(stages[0].nf);
    let gainAccum = RFCalc.dbToLinear(stages[0].gain);

    for (let i = 1; i < stages.length; i++) {
      const nf = RFCalc.dbToLinear(stages[i].nf);
      const gain = RFCalc.dbToLinear(stages[i].gain);
      nfLinear += (nf - 1) / gainAccum;
      gainAccum *= gain;
    }

    return RFCalc.linearToDb(nfLinear);
  },

  /**
   * Cascaded IP3 calculation using proper recursive formula
   * Formula: OiP3_C = 1 / (1/(IP3_{N-1}*G_N) + 1/IP3_N) [mW]
   * stages = [{gain: dB, oip3: dBm}, ...]
   */
  cascadeIP3: (stages) => {
    if (stages.length === 0) return Infinity;

    // Find first stage with finite OIP3 (skip leading passive stages)
    let startIndex = 0;
    while (startIndex < stages.length && !isFinite(stages[startIndex].oip3)) {
      startIndex++;
    }

    if (startIndex >= stages.length) return Infinity;

    // Convert first active stage
    let cascadedOIP3_mW = RFCalc.dbmToWatts(stages[startIndex].oip3) * 1000; // Convert to mW

    // Recursively cascade each subsequent stage
    for (let i = startIndex + 1; i < stages.length; i++) {
      const oip3_i_mW = RFCalc.dbmToWatts(stages[i].oip3) * 1000; // mW
      const gain_i_linear = RFCalc.dbToLinear(stages[i].gain);

      if (isFinite(oip3_i_mW) && oip3_i_mW > 0 && isFinite(cascadedOIP3_mW) && cascadedOIP3_mW > 0) {
        // Apply formula: OiP3_C = 1 / (1/(IP3_{prev}*G_i) + 1/IP3_i)
        const term1 = 1 / (cascadedOIP3_mW * gain_i_linear);
        const term2 = 1 / oip3_i_mW;
        cascadedOIP3_mW = 1 / (term1 + term2);
      }
    }

    if (!isFinite(cascadedOIP3_mW) || cascadedOIP3_mW <= 0) return Infinity;

    // Convert from mW to dBm
    return 10 * Math.log10(cascadedOIP3_mW);
  },

  /**
   * Cascaded P1dB calculation using proper compression formula
   * Formula: Total P1dB = 10*log10[(1/(P1dB_1*Gain_2*...*Gain_N) + 1/(P1dB_2*Gain_3*...*Gain_N) + ... + 1/P1dB_N)^-1]
   * where P1dB values are in linear (mW) and Gains are in linear (not dB)
   */
  cascadeP1dB: (stages) => {
    if (stages.length === 0) return Infinity;

    // Convert all P1dB values from dBm to mW and gains from dB to linear
    const stagesLinear = stages.map(stage => ({
      p1db_mW: RFCalc.dbmToWatts(stage.p1db) * 1000, // Convert to mW
      gain_linear: RFCalc.dbToLinear(stage.gain)
    }));

    // Calculate the sum of inverse terms
    let sumInverse = 0;

    for (let i = 0; i < stagesLinear.length; i++) {
      const p1db_i = stagesLinear[i].p1db_mW;

      // Calculate product of gains after stage i (Gain_{i+1} * Gain_{i+2} * ... * Gain_N)
      let gainProduct = 1;
      for (let j = i + 1; j < stagesLinear.length; j++) {
        gainProduct *= stagesLinear[j].gain_linear;
      }

      // Add term: 1 / (P1dB_i * Gain_{i+1} * ... * Gain_N)
      if (isFinite(p1db_i) && p1db_i > 0) {
        sumInverse += 1 / (p1db_i * gainProduct);
      }
    }

    if (sumInverse === 0) return Infinity;

    // Total P1dB in mW
    const totalP1dB_mW = 1 / sumInverse;

    // Convert back to dBm
    return 10 * Math.log10(totalP1dB_mW);
  },

  // ============================================================================
  // COMPRESSION & NONLINEARITY
  // ============================================================================

  /**
   * Calculate compression for a single stage
   * Returns compression in dB
   */
  calculateCompression: (inputPower, gain, p1db) => {
    if (!isFinite(p1db)) return 0;

    const outputPower = inputPower + gain;
    const backoff = p1db - outputPower; // dB

    if (backoff > 10) {
      // Far from compression
      return 0;
    } else if (backoff <= 0) {
      // At or beyond P1dB
      return 1.0 + Math.abs(backoff) / 10; // Simplified model
    } else {
      // Approaching compression (0-10dB backoff)
      // Quadratic model: compression increases as backoff decreases
      return Math.pow((10 - backoff) / 10, 2);
    }
  },

  /**
   * Get compression state for visualization based on output power vs P1dB
   * Returns: 'normal', 'warning', 'compressed', 'saturated'
   * Thresholds:
   * - Normal: Pout <= P1dB - 0.5 dB
   * - Warning (orange): P1dB - 0.5 dB < Pout <= P1dB + 0.5 dB
   * - Compressed (red): P1dB + 0.5 dB < Pout < P1dB + 2 dB
   * - Saturated: Pout >= P1dB + 2 dB (output clamped)
   */
  getCompressionState: (outputPower, p1db) => {
    if (!isFinite(outputPower) || !isFinite(p1db)) return 'normal';

    const pout = parseFloat(outputPower);
    const p1dbValue = parseFloat(p1db);

    if (pout >= p1dbValue + 2) return 'saturated'; // Hard limit
    if (pout > p1dbValue + 0.5) return 'compressed'; // Red
    if (pout > p1dbValue - 0.5) return 'warning'; // Orange
    return 'normal';
  },

  /**
   * Limit output power to saturation level (P1dB + 2dB)
   */
  limitToSaturation: (outputPower, p1db) => {
    if (!isFinite(outputPower) || !isFinite(p1db)) return outputPower;

    const pout = parseFloat(outputPower);
    const p1dbValue = parseFloat(p1db);
    const saturationLimit = p1dbValue + 2;

    return Math.min(pout, saturationLimit);
  },

  // ============================================================================
  // INTERCEPT POINTS
  // ============================================================================

  oip3ToIip3: (oip3, gain) => {
    if (!isFinite(oip3)) return Infinity;
    return oip3 - gain;
  },

  iip3ToOip3: (iip3, gain) => {
    if (!isFinite(iip3)) return Infinity;
    return iip3 + gain;
  },

  // ============================================================================
  // NOISE CALCULATIONS
  // ============================================================================

  /**
   * Calculate noise floor
   * noiseFigure in dB, bandwidth in Hz
   * Returns noise power in dBm
   */
  noiseFloor: (noiseFigure, bandwidth) => {
    // Thermal noise: -174 dBm/Hz at room temperature
    const thermalNoise = -174; // dBm/Hz
    const noisePower = thermalNoise + 10 * Math.log10(bandwidth) + noiseFigure;
    return noisePower;
  },

  /**
   * Signal-to-noise ratio
   */
  snr: (signalPower, noiseFloor) => {
    return signalPower - noiseFloor;
  },

  /**
   * Spurious-free dynamic range
   */
  spuriousFreeDynamicRange: (noiseFloor, iip3) => {
    if (!isFinite(iip3)) return Infinity;
    return (2/3) * (iip3 - noiseFloor);
  },

  // ============================================================================
  // LINK BUDGET
  // ============================================================================

  /**
   * Calculate free-space path loss (Friis equation)
   * frequency in MHz, distance in km
   * Returns FSPL in dB
   */
  freeSpacePathLoss: (frequency, distance) => {
    return 32.45 + 20 * Math.log10(frequency) + 20 * Math.log10(distance);
  },

  /**
   * Complete link budget calculation
   */
  linkBudget: (params) => {
    const {
      txPower,      // dBm
      txGain,       // dB
      rxGain,       // dB
      frequency,    // MHz
      distance,     // km
      additionalLosses // array of dB values
    } = params;

    const fspl = RFCalc.freeSpacePathLoss(frequency, distance);
    const totalLosses = additionalLosses.reduce((sum, loss) => sum + loss, 0);
    const rxPower = txPower + txGain + rxGain - fspl - totalLosses;

    return {
      fspl,
      totalLosses,
      rxPower
    };
  },

  // ============================================================================
  // SMITH CHART & IMPEDANCE
  // ============================================================================

  /**
   * Calculate reflection coefficient from impedance
   * z = {real, imag}, z0 = reference impedance (default 50Ω)
   */
  reflectionCoefficient: (z, z0 = 50) => {
    const numerator = {
      real: z.real - z0,
      imag: z.imag
    };
    const denominator = {
      real: z.real + z0,
      imag: z.imag
    };

    const denMag2 = denominator.real ** 2 + denominator.imag ** 2;

    if (denMag2 === 0) return { real: 0, imag: 0 };

    return {
      real: (numerator.real * denominator.real + numerator.imag * denominator.imag) / denMag2,
      imag: (numerator.imag * denominator.real - numerator.real * denominator.imag) / denMag2
    };
  },

  /**
   * Reflection coefficient magnitude
   */
  reflectionMagnitude: (gamma) => {
    return Math.sqrt(gamma.real ** 2 + gamma.imag ** 2);
  },

  /**
   * VSWR from reflection coefficient
   */
  gammaToVSWR: (gamma) => {
    const mag = RFCalc.reflectionMagnitude(gamma);
    if (mag >= 1) return Infinity;
    return (1 + mag) / (1 - mag);
  },

  /**
   * Return loss from VSWR
   */
  vswrToReturnLoss: (vswr) => {
    if (vswr < 1) return Infinity;
    return -20 * Math.log10((vswr - 1) / (vswr + 1));
  },

  /**
   * Return loss from reflection coefficient
   */
  returnLoss: (gamma) => {
    const mag = RFCalc.reflectionMagnitude(gamma);
    if (mag === 0) return Infinity;
    return -20 * Math.log10(mag);
  },

  /**
   * Impedance from reflection coefficient
   */
  gammaToImpedance: (gamma, z0 = 50) => {
    const numerator = {
      real: 1 + gamma.real,
      imag: gamma.imag
    };
    const denominator = {
      real: 1 - gamma.real,
      imag: -gamma.imag
    };

    const denMag2 = denominator.real ** 2 + denominator.imag ** 2;

    if (denMag2 === 0) return { real: Infinity, imag: Infinity };

    const z = {
      real: (numerator.real * denominator.real + numerator.imag * denominator.imag) / denMag2,
      imag: (numerator.imag * denominator.real - numerator.real * denominator.imag) / denMag2
    };

    return {
      real: z.real * z0,
      imag: z.imag * z0
    };
  },

  // ============================================================================
  // PASSIVE COMPONENT CALCULATIONS
  // ============================================================================

  /**
   * Cable loss calculation
   * length in meters, lossPerMeter in dB/m
   */
  cableLoss: (length, lossPerMeter) => {
    return length * lossPerMeter;
  },

  /**
   * Cable electrical length
   * frequency in MHz, length in meters, velocityFactor (0-1)
   * Returns electrical length in degrees
   */
  cableElectricalLength: (frequency, length, velocityFactor) => {
    const wavelength = (300 / frequency) * velocityFactor; // meters
    const electricalLength = (length / wavelength) * 360; // degrees
    return electricalLength % 360;
  },

  /**
   * Cable time delay
   * length in meters, velocityFactor (0-1)
   * Returns delay in nanoseconds
   */
  cableTimeDelay: (length, velocityFactor) => {
    const speedOfLight = 3e8; // m/s
    const propagationSpeed = speedOfLight * velocityFactor;
    const delay = (length / propagationSpeed) * 1e9; // convert to ns
    return delay;
  },

  /**
   * Splitter theoretical loss
   * N-way splitter has theoretical loss of 10*log10(N)
   */
  splitterTheoreticalLoss: (numWays) => {
    return 10 * Math.log10(numWays);
  }
};

export default RFCalc;
